import { Add } from "@mui/icons-material";
import { Card, Grid } from "@mui/material";

import Helpers from "../../../commons/helpers";
import Autocomplete from "../../../components/Autocomplete";
import Box from "../../../components/Box";
import Button from "../../../components/Button";
import ControlChip from "../../../components/Chip";
import CustomStatusCell from "../../../components/CustomStatusCell";
import DataTable from "../../../components/DataTable";
import FormField from "../../../components/FormField";
import useStrings from "../../../hooks/useStrings";

import { useMemo } from "react";
import { ICodename, IPropListContainer } from "../../../commons/interfaces";
import { RoleHelpers } from "../../../commons/roleHelpers";
import { Constants } from "../../../constants/constants";
import { Mode } from "../../../constants/enums";
import { IListPolicyModel, useData, useSelectData } from "../../../hooks/policy/list";
import { useCommonComponentContext } from "../../../providers/CommonComponentProvider";
import { IRequestGetPagedPolicy } from "../../../services/identity/policy.service";
import { MainComponent } from "../../main";

export const PolicyListComponent = ({
    hidenActionCreate,
    hidenActionDelete,
    hidenActionUpdate,
    requestGetPaged,
    onGetPaged,
    onNavigate,
}: IPropListContainer<IRequestGetPagedPolicy> & {
    hidenActionUpdate?: boolean;
    hidenActionDelete?: boolean;
    hidenActionCreate?: boolean;
}) => {
    const Strings = useStrings();
    const { userInfo, serviceCode } = useCommonComponentContext();

    const { services } = useSelectData();
    const { rows, model, loadingDataTable, getPaged, handleDelete, onChangeValue, onResetModel } = useData({
        request: requestGetPaged,
        onGetPaged,
    });

    const columns = useMemo(
        () => [
            { Header: Strings.POLICY.CODE, accessor: "policyCode", width: "80px" },
            { Header: Strings.POLICY.NAME, accessor: "policyName", width: "120px" },
            { Header: Strings.POLICY.DESCRIPTION, accessor: "description", width: 170 },
            // {
            //     Header: Strings.Common.STATUS,
            //     accessor: "status",
            //     width: "170px",
            //     Cell: (row: any) => <CustomStatusCell status={Number(row.value)} />,
            // },
        ],
        [Strings.getLanguage()]
    );

    const isServiceAdmin = RoleHelpers.isServiceAdmin(userInfo?.userProfile?.roleType);

    return (
        <MainComponent>
            <Grid container spacing={2}>
                {/* {!hidenActionCreate && isServiceAdmin && (
                    <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "end" }}>
                            <Button
                                onClick={() => {
                                    onNavigate({ mode: Mode.Create });
                                }}
                            >
                                <Add /> &nbsp;
                                {Strings.Common.ADD_NEW}
                            </Button>
                        </Box>
                    </Grid>
                )}
                <Grid item xs={12}>
                    {!Helpers.isNullOrEmpty(model.requestData?.serviceCode) && isServiceAdmin && (
                        <ControlChip
                            label={Strings.POLICY.SERVICE}
                            value={services.find((el) => el.code === model.requestData?.serviceCode)?.name}
                            onDelete={
                                serviceCode === model.requestData?.serviceCode
                                    ? undefined
                                    : () => {
                                          getPaged({ ...model.requestData, serviceCode: serviceCode });
                                      }
                            }
                        />
                    )}
                    {!Helpers.isNullOrEmpty(model.requestData?.searchText) && (
                        <ControlChip
                            label={Strings.POLICY.SEARCH_TEXT}
                            value={model.requestData?.searchText}
                            onDelete={() => {
                                getPaged({ ...model.requestData, searchText: "" });
                            }}
                        />
                    )}
                </Grid> */}
                <Grid item xs={12}>
                    <Card>
                        <Box p={2}>
                            <DataTable
                                loading={loadingDataTable}
                                totalCount={model.requestData?.totalCount || 0}
                                pageNumber={model.requestData?.pageNumber || 1}
                                rowPerPage={model.requestData?.pageSize || Constants.ROW_PER_PAGE}
                                onChangePageSize={(pageSize) => getPaged({ ...model.requestData, pageSize })}
                                onChangePageNumber={(pageNumber) => getPaged({ ...model.requestData, pageNumber })}
                                //
                                searchText={model.requestData?.searchText}
                                placeholderSearch={Strings.POLICY.SEARCH_TEXT}
                                onSearch={(val) => getPaged({ ...model.requestData, pageNumber: 1, searchText: val })}
                                onSearchChange={onChangeValue("searchText")}
                                filterForm={
                                    <FilterForm
                                        {...{
                                            model,
                                            services,
                                            onChangeValue,
                                        }}
                                    />
                                }
                                onFilter={() => getPaged({ ...model.requestDataTemp, pageNumber: 1 })}
                                onReset={() =>
                                    getPaged({
                                        pageNumber: 1,
                                        searchText: "",
                                    })
                                }
                                onCloseFilter={onResetModel}
                                //
                                table={{
                                    columns,
                                    rows,
                                }}
                                actionList={(row: any) => {
                                    return [
                                        {
                                            key: "detail",
                                            iconName: "view",
                                            title: Strings.Common.VIEW_DETAIL,
                                            callback: (row: any) => onNavigate({ id: row.id, mode: Mode.View }),
                                        },

                                        !hidenActionUpdate && {
                                            key: "edit",
                                            iconName: "edit",
                                            title: Strings.Common.EDIT,
                                            callback: (row: any) => onNavigate({ id: row.id, mode: Mode.Update }),
                                        },

                                        !hidenActionDelete && {
                                            key: "delete",
                                            iconName: "delete",
                                            title: Strings.Common.DELETE,
                                            callback: (row: any) => handleDelete(row),
                                        },
                                    ];
                                }}
                            />
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </MainComponent>
    );
};

const FilterForm = ({
    services,
    model,
    onChangeValue,
}: {
    services: ICodename[];

    model: IListPolicyModel;

    onChangeValue: (key: keyof IRequestGetPagedPolicy) => (value: string | number) => void;
}) => {
    const Strings = useStrings();
    const { userInfo } = useCommonComponentContext();

    return (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <FormField
                        maxLength={255}
                        label={Strings.POLICY.SEARCH_TEXT}
                        placeholder={Strings.POLICY.ENTER_SEARCH_TEXT}
                        defaultValue={model.requestDataTemp?.searchText}
                        onBlur={onChangeValue("searchText")}
                    />
                </Grid>
                {RoleHelpers.isServiceAdmin(userInfo?.userProfile?.roleType) && (
                    <Grid item xs={12}>
                        <Autocomplete
                            isSelectedBox
                            data={services || []}
                            label={Strings.POLICY.SERVICE}
                            placeholder={Strings.POLICY.SELECT_SERVICE}
                            defaultValue={model.requestDataTemp?.serviceCode}
                            onChange={onChangeValue("serviceCode")}
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default PolicyListComponent;
